import { Component, OnInit, Output, EventEmitter, Input } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import {
	PostcodeValidator,
	StreetNameValidator,
	StreetNumberValidator,
	UnitNumberSuffixValidator,
	UnitNumberValidator,
} from "@dodo/helper";
import {
	AddressService,
	ButtonThemeState,
	ManualAddress,
	OnDestroyedDirective,
	StreetType,
	Suburb,
	UnitType,
} from "app/shared";
import { debounceTime, filter, takeUntil } from "rxjs/operators";

@Component({
    selector: "app-manual-address",
    templateUrl: "./manual-address.component.html",
    styleUrls: ["./manual-address.component.scss"],
    standalone: false
})
export class ManualAddressComponent
	extends OnDestroyedDirective
	implements OnInit {
	public showFullAddress: boolean;
	public buttonTheme = ButtonThemeState.PURPLE;
	public unitTypes: UnitType[];
	public streetTypes: StreetType[];
	public suburbs: Suburb[];
	public formValid: boolean;
	public busy = false;
	public form: FormGroup;
	private selectedPostcode: Suburb;
	conditionalRequired = false;
	isOptionSelected = false;
	@Input() address: any;
	@Input() isQualification: boolean;
	@Input() isMule: boolean;
	@Output() submitAddress = new EventEmitter<ManualAddress>();
	@Output() back = new EventEmitter<boolean>();
	@Output() invalidAddress = new EventEmitter<boolean>();

	constructor(
		private formBuilder: FormBuilder,
		private addressService: AddressService
	) {
		super();
	}

	ngOnInit(): void {
		this.getAddressData();
		this.buildForm(this.address);
		this.subscribeToForm();
		this.handlePostcodeFilter();
	}

	_handleUnitNoError(error: any): any {
		return error?.unitNumberInvalid?.message;
	}

	_handleUnitNoSuffixError(error: any): any {
		return error?.unitNumberSuffixInvalid?.message;
	}

	_handleStreetNoError(error: any): any {
		return error?.streetNumberInvalid?.message;
	}
	_handleStreetNameError(error: any): any {
		return error?.streetNameInvalid?.message;
	}
	_handleUnitTypeError(error: any): any {
		if (error && error?.invalidValue === true) {
			return "Please select an option";
		} else {
			return error?.unitTypeInvalid?.message;
		}

	}
	_handleStreetTypeError(error: any): any {
		if (error && error?.invalidValue === true) {
			return "Please select an option";
		} else {
			return error?.streetTypeInvalid?.message;
		}
	}

	_handlePostcodeError(error: any): any {
		if (error && error?.invalidValue === true) {
			return "Please select an option";
		} else {
			return error?.postcodeInvalid?.message;
		}
	}

	private getAddressData(): void {
		this.addressService
			.getManualAddressData(this.isMule)
			.pipe(takeUntil(this.unsubscribeAll))
			.subscribe((data) => {
				if (this.isMule) {
					this.unitTypes = data?.[0].map((item) => {
						return {
							Key: item.value,
							Value: item.name,
						};
					});
					this.streetTypes = data?.[1].map((item) => {
						return {
							Key: item.value,
							Value: item.name,
						};
					});
				} else {
					this.unitTypes = data?.[0];
					this.streetTypes = data?.[1];
				}
			});
	}

	private buildForm(data: any): void {
		if (data?.state && data?.postcode && data.suburb) {
			this.selectedPostcode = { Suburb: data.suburb, Postcode: data?.postcode, State: data?.state };
		}
		this.showFullAddress = (data && data?.showFullAddress === true) ? true : this.showFullAddress;
		this.form = this.formBuilder.group({
			unitType: [data?.unitType || ""],
			unitNo: [data?.unitNo || "", [UnitNumberValidator]],
			unitNoSuffix: [
				data?.unitNoSuffix || "",
				[
					Validators.maxLength(1),
					(val) => UnitNumberSuffixValidator(val, "unit"),
				],
			],
			streetNo: [
				data?.streetNo || "",
				[
					Validators.required,
					Validators.maxLength(10),
					StreetNumberValidator,
				],
			],
			streetNoSuffix: [
				data?.streetNoSuffix || "",
				[
					Validators.maxLength(1),
					(val) => UnitNumberSuffixValidator(val, "street"),
				],
			],
			streetName: [
				data?.streetName || "",
				[Validators.required, StreetNameValidator],
			],
			streetType: [data?.streetType || "", Validators.required],
			postcode: [
				data?.postcode || "",
				[Validators.required, PostcodeValidator],
			],
			suburb: [
				{ value: data?.suburb || "", disabled: true },
				Validators.required,
			],
			state: [
				{ value: data?.state || "", disabled: true },
				Validators.required,
			],
		});
	}
	private subscribeToForm(): void {
		this.form.statusChanges
			.pipe(takeUntil(this.unsubscribeAll))
			.subscribe((status) => {
				this.formValid = status === "VALID";
				if (
					this.formValid &&
					!this.isQualification
				) {
					this.submitManualAddress();
				} else if (
					(status === "INVALID")
				) {
					this.invalidAddress.emit(true);
				}
			});

		this.form.controls.unitType.valueChanges.subscribe((unitType) => {
			if (unitType) {
				const checkUnitType = this.unitTypes.find((unit) => unit.Value === unitType);
				if (checkUnitType === undefined) {
					this.form.get("unitType").setErrors({ invalidValue: true });

				} else {
					if (unitType !== "Not Applicable" &&
						this.form.controls?.unitType?.status === "VALID") {
						this.conditionalRequired = true;
						this.form.controls.unitNo.setValidators([Validators.required, UnitNumberValidator]);

					} else {
						this.conditionalRequired = false;
						this.form.controls.unitNo.setValidators([UnitNumberValidator]);

					}

				}

			} else {
				this.conditionalRequired = false;
				this.form.controls.unitNo.setValidators(UnitNumberValidator);
			}
			this.form.controls.unitNo.updateValueAndValidity();
		});
		this.form.controls.streetType.valueChanges.subscribe((streetType) => {
			const checkStreetType = this.streetTypes.find((street) => street.Value === streetType);
			if (checkStreetType === undefined) {
				this.form.get("streetType").setErrors({ invalidValue: true });

			}
		});
	}

	public handlePostcodeSelection(option: any): void {
		this.selectedPostcode = option;
		this.form.controls.postcode.setValue(option.Postcode);
		this.form.controls.suburb.setValue(option.Suburb);
		this.form.controls.state.setValue(option.State);
	}

	public handlePostcodeFilter(): void {
		this.form.controls.postcode.valueChanges
			.pipe(
				takeUntil(this.unsubscribeAll),
				filter((query: string) => query?.length > 2)
			)
			.subscribe((postcode) => {
				if (postcode.length) {
					this.addressService
						.suburbFromPostcode(this.isMule, postcode)
						.pipe(
							debounceTime(400),
							takeUntil(this.unsubscribeAll),
							filter((x) => (x == null || !!x))
						)
						.subscribe(
							(suburbs) => {
								if (suburbs) {
									if (suburbs?.length === 0) {
										this.form.controls.suburb.setValue("");
										this.form.controls.state.setValue("");
									} else {
										this.isOptionSelected = suburbs.some(
											(unit) =>
												unit.Suburb ===
												this.form.controls.suburb.value
										);
									}
									if (this.isMule) {
										this.suburbs = suburbs.map((item) => {
											return {
												Suburb: item.name,
												Postcode: item.characteristic[0]?.value,
												State: item.characteristic[1]?.value,
											};
										});
										const checkSPostCode = this.suburbs.find((suburb) => suburb.Postcode === postcode && suburb.Suburb === this.form.controls?.suburb?.value);
										if (checkSPostCode === undefined) {
											this.form.get("postcode").setErrors({ invalidValue: true });

										}
									} else {
										this.suburbs = suburbs;
									}
								} else {
									this.form.controls.suburb.setValue("");
									this.form.controls.state.setValue("");
									this.form.get("postcode").setErrors({ invalidValue: true });
								}
							},
							err => console.log("error : " + err));
				}
			}, err => console.log("error : " + err));
	}

	public submitManualAddress(): void {
		const formData = this.form.getRawValue();
		const retVal: ManualAddress = {
			unitType: formData.unitType,
			unitNo: formData.unitNo,
			unitNoSuffix: formData.unitNoSuffix,
			streetNo: formData.streetNo,
			streetNoSuffix: formData.streetNoSuffix,
			streetName: formData.streetName,
			streetType: formData.streetType,
			suburb: formData.suburb,
			state: formData.state,
			postcode: this.selectedPostcode,
			showFullAddress: this.showFullAddress
		};
		if (retVal.unitType) {
			retVal.unitTypeKey = this.unitTypes.find(
				(unitType) => unitType.Value === formData.unitType
			)?.Key;
		}
		if (retVal.streetType) {
			retVal.streetTypeKey = this.streetTypes.find(
				(streetType) => streetType.Value === formData.streetType
			)?.Key;
		}
		this.submitAddress.emit(retVal);
	}

	public goBack(): void {
		this.back.emit(true);
	}

	toggleAddress() {
		this.showFullAddress = !this.showFullAddress;
		if (this.form?.status === "VALID") {
			this.submitManualAddress();
		}
	}
}
